/**
 * Les quatre familles de reglages d'un agregateur ajoute (lot F, item F1).
 *
 * David a cree un agregateur et n'a rien pu en faire : le formulaire ne portait que
 * son nom, son logo et sa route, et promettait en petits caracteres que « le bareme
 * peut etre configure ensuite ». Il ne pouvait pas l'etre. Le modele economique du
 * produit est UN BAREME PAR AGREGATEUR : sans ces reglages, une fiche est une coquille.
 *
 * Le composant vit a part de `AggregateursPage` : la page tenait en 299 lignes, ce bloc
 * en ajoute une centaine, et les deux se relisent mieux separes.
 *
 * ⚠️ CES REGLAGES SONT CONSERVES, ILS NE PRODUISENT ENCORE AUCUN REVENU CALCULE.
 * Les deux agregateurs integres portent chacun leur moteur (services/survoltage.py,
 * services/symphonics_revenus.py). Un moteur generique qui lirait ce bareme-ci est un
 * chantier a part entiere. Le bandeau du bas le dit a l'ecran, et ce n'est pas de la
 * politesse : un formulaire qui range des montants sans que personne ne les lise est
 * exactement le piege qu'un lot qui touche a des montants doit eviter.
 */
import { Label } from '@/shared/ui/label';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { Info, Plus, X } from 'lucide-react';

// Ferme, comme cote serveur (`catalogue.TYPES_ACTIFS_CONNUS`). Un type invente ici ne
// correspondrait a aucun actif du parc, et la ligne de bareme ne s'appliquerait jamais.
export const TYPES_ACTIFS = ['PAC', 'PV', 'Ballon', 'Batterie'];

/** Une part se saisit en pourcentage et se stocke entre 0 et 1. */
const versPart = (pourcent) => Math.max(0, Math.min(100, Number(pourcent) || 0)) / 100;
const versPourcent = (part) => Math.round((Number(part) || 0) * 100);

/** `[{cle, bas, haut}]` <-> `{cle: {bas, haut}}`. L'ecran edite des lignes, l'API un objet. */
export function baremeVersLignes(bareme) {
  return Object.entries(bareme || {}).map(([cle, v]) => ({
    cle,
    bas: v?.bas ?? '',
    haut: v?.haut ?? '',
  }));
}

export function lignesVersBareme(lignes) {
  const sortie = {};
  (lignes || []).forEach(({ cle, bas, haut }) => {
    const nom = (cle || '').trim();
    if (!nom) return;
    sortie[nom] = { bas: Number(bas) || 0, haut: Number(haut) || 0 };
  });
  return sortie;
}

export function decotesVersLignes(decotes) {
  return Object.entries(decotes || {}).map(([cle, part]) => ({ cle, pourcent: versPourcent(part) }));
}

export function lignesVersDecotes(lignes) {
  const sortie = {};
  (lignes || []).forEach(({ cle, pourcent }) => {
    const nom = (cle || '').trim();
    if (!nom) return;
    sortie[nom] = versPart(pourcent);
  });
  return sortie;
}

function Ligne({ children, onRetirer }) {
  return (
    <div className="flex items-center gap-2">
      {children}
      <button
        type="button" onClick={onRetirer} aria-label="Retirer cette ligne"
        className="p-1 rounded text-neutral-400 hover:text-red-500 hover:bg-red-50 transition-colors"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export default function ConfigurationAgregateur({ valeur, onChange }) {
  const maj = (champ, v) => onChange({ ...valeur, [champ]: v });

  const basculerType = (type) => {
    const actuels = valeur.types_actifs || [];
    maj('types_actifs', actuels.includes(type)
      ? actuels.filter(t => t !== type)
      : [...actuels, type]);
  };

  const majLigne = (champ, index, cle, v) => {
    const lignes = [...(valeur[champ] || [])];
    lignes[index] = { ...lignes[index], [cle]: v };
    maj(champ, lignes);
  };

  const retirerLigne = (champ, index) =>
    maj(champ, (valeur[champ] || []).filter((_, i) => i !== index));

  return (
    <div className="space-y-4 pt-3 border-t border-neutral-200" data-testid="agg-configuration">
      <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">
        Configuration du barème
      </p>

      {/* --- Actifs concernes -------------------------------------------------- */}
      <div>
        <Label className="text-[11px] text-neutral-500">Types d&apos;actifs concernés</Label>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {TYPES_ACTIFS.map(type => {
            const coche = (valeur.types_actifs || []).includes(type);
            return (
              <button
                key={type} type="button" onClick={() => basculerType(type)}
                data-testid={`agg-type-${type}`}
                className={`px-3 h-8 rounded-md border text-[12px] transition-colors ${
                  coche ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                        : 'border-neutral-200 text-neutral-500 hover:border-neutral-400'}`}
              >
                {type}
              </button>
            );
          })}
        </div>
      </div>

      {/* --- Parametres de calcul ---------------------------------------------- */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] text-neutral-500">État du parc (%)</Label>
          <Input
            type="number" min="0" max="100" className="h-9"
            value={versPourcent(valeur.parametres_calcul?.etat_parc)}
            onChange={e => maj('parametres_calcul', {
              ...(valeur.parametres_calcul || {}), etat_parc: versPart(e.target.value) })}
            data-testid="agg-etat-parc"
          />
          <p className="text-[10px] text-neutral-400 mt-1">Part du parc effectivement pilotable.</p>
        </div>
        <div>
          <Label className="text-[11px] text-neutral-500">Part opérateur (%)</Label>
          <Input
            type="number" min="0" max="100" className="h-9"
            value={versPourcent(valeur.parametres_calcul?.part_operateur)}
            onChange={e => maj('parametres_calcul', {
              ...(valeur.parametres_calcul || {}), part_operateur: versPart(e.target.value) })}
            data-testid="agg-part-operateur"
          />
          <p className="text-[10px] text-neutral-400 mt-1">Part du brut conservée par l&apos;opérateur.</p>
        </div>
      </div>

      {/* --- Regles de remuneration -------------------------------------------- */}
      <div>
        <Label className="text-[11px] text-neutral-500">
          Règles de rémunération (€/an, fourchette basse et haute)
        </Label>
        <div className="space-y-2 mt-1.5" data-testid="agg-remuneration">
          {(valeur.remuneration_lignes || []).map((ligne, i) => (
            <Ligne key={i} onRetirer={() => retirerLigne('remuneration_lignes', i)}>
              <Input
                className="h-8 text-[12px] flex-1" placeholder="Ex. mecapa"
                value={ligne.cle}
                onChange={e => majLigne('remuneration_lignes', i, 'cle', e.target.value)}
              />
              <Input
                type="number" className="h-8 text-[12px] w-24 text-right" placeholder="bas"
                value={ligne.bas}
                onChange={e => majLigne('remuneration_lignes', i, 'bas', e.target.value)}
              />
              <Input
                type="number" className="h-8 text-[12px] w-24 text-right" placeholder="haut"
                value={ligne.haut}
                onChange={e => majLigne('remuneration_lignes', i, 'haut', e.target.value)}
              />
            </Ligne>
          ))}
        </div>
        <Button
          type="button" variant="outline" size="sm" className="mt-2 h-8"
          onClick={() => maj('remuneration_lignes',
            [...(valeur.remuneration_lignes || []), { cle: '', bas: '', haut: '' }])}
          data-testid="agg-add-remuneration"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une règle
        </Button>
      </div>

      {/* --- Decotes ------------------------------------------------------------ */}
      <div>
        <Label className="text-[11px] text-neutral-500">Décotes appliquées (%)</Label>
        <div className="space-y-2 mt-1.5" data-testid="agg-decotes">
          {(valeur.decotes_lignes || []).map((ligne, i) => (
            <Ligne key={i} onRetirer={() => retirerLigne('decotes_lignes', i)}>
              <Input
                className="h-8 text-[12px] flex-1" placeholder="Ex. ajustement"
                value={ligne.cle}
                onChange={e => majLigne('decotes_lignes', i, 'cle', e.target.value)}
              />
              <Input
                type="number" min="0" max="100" className="h-8 text-[12px] w-24 text-right"
                value={ligne.pourcent}
                onChange={e => majLigne('decotes_lignes', i, 'pourcent', e.target.value)}
              />
            </Ligne>
          ))}
        </div>
        <Button
          type="button" variant="outline" size="sm" className="mt-2 h-8"
          onClick={() => maj('decotes_lignes',
            [...(valeur.decotes_lignes || []), { cle: '', pourcent: 0 }])}
          data-testid="agg-add-decote"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une décote
        </Button>
      </div>

      <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-2.5"
           data-testid="agg-config-avertissement">
        <Info className="w-3.5 h-3.5 text-amber-700 shrink-0 mt-0.5" />
        <p className="text-[10.5px] text-amber-800 leading-relaxed">
          Ces réglages sont <strong>enregistrés mais pas encore calculés</strong>. Survoltage
          et Symphonics ont chacun leur propre moteur de calcul ; aucun montant affiché
          ailleurs dans l&apos;application ne dérive pour l&apos;instant de ce barème.
        </p>
      </div>
    </div>
  );
}
